/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 *
 * Not: React Fast Refresh için Context tanımı ve `useTheme` hook'u Provider'dan
 * (`ThemeContext.tsx`) ayrı dosyalarda durur (`theme-context.ts` + `useTheme.ts`).
 * Aksi halde Provider dosyası hem component hem hook export ettiğinden HMR boundary
 * kaybolur ve "must be used within a ThemeProvider" benzeri kopuk context görülebilir.
 */

import { createContext, type Context } from 'react';

export type Theme = 'light' | 'dark';

export interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const THEME_CONTEXT_GLOBAL_KEY = 'qubepro_theme_context';

function getStableThemeContext(): Context<ThemeContextType | undefined> {
  const g = globalThis as typeof globalThis & {
    [THEME_CONTEXT_GLOBAL_KEY]?: Context<ThemeContextType | undefined>;
  };
  if (!g[THEME_CONTEXT_GLOBAL_KEY]) {
    g[THEME_CONTEXT_GLOBAL_KEY] = createContext<ThemeContextType | undefined>(undefined);
  }
  return g[THEME_CONTEXT_GLOBAL_KEY]!;
}

export const ThemeContext = getStableThemeContext();

export const THEME_STORAGE_KEY = 'qubepro_theme';

let themeTransitionGuardFrame: number | null = null;
let themeTransitionGuardTimeout: number | null = null;

function releaseThemeTransitionGuard() {
  if (typeof window === 'undefined') return;
  document.documentElement.classList.remove('theme-changing');

  if (themeTransitionGuardFrame !== null) {
    window.cancelAnimationFrame(themeTransitionGuardFrame);
    themeTransitionGuardFrame = null;
  }
  if (themeTransitionGuardTimeout !== null) {
    window.clearTimeout(themeTransitionGuardTimeout);
    themeTransitionGuardTimeout = null;
  }
}

function armThemeTransitionGuard() {
  if (typeof window === 'undefined') return;

  releaseThemeTransitionGuard();
  document.documentElement.classList.add('theme-changing');

  themeTransitionGuardFrame = window.requestAnimationFrame(() => {
    themeTransitionGuardFrame = window.requestAnimationFrame(releaseThemeTransitionGuard);
  });
  themeTransitionGuardTimeout = window.setTimeout(releaseThemeTransitionGuard, 180);
}

export function applyThemeToDocument(next: Theme) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  if (root.getAttribute('data-theme') !== next) {
    armThemeTransitionGuard();
  }
  root.setAttribute('data-theme', next);
  localStorage.setItem(THEME_STORAGE_KEY, next);
  document.body.style.backgroundColor = '';
  document.body.style.color = '';
}
